<template>
  <div class="panel">
    <div class="panel-header">
      <div class="panel-title">
        {{ $t('hit-admin.notifications.tab-history') }}
      </div>
      <hit-button
        :label="$t('hit-admin.notifications.refresh-history')"
        color="button"
        prefix-icon="refresh"
        :disabled="loading"
        @click="reload"
      />
    </div>

    <div v-if="loading" class="muted p-4">Chargement…</div>
    <div v-else-if="!rows.length" class="muted p-4">Aucune diffusion.</div>
    <div v-else class="table-wrap">
      <table class="broadcast-table">
        <thead>
          <tr>
            <th>{{ $t('hit-admin.notifications.date') }}</th>
            <th>{{ $t('hit-admin.notifications.status') }}</th>
            <th>{{ $t('hit-admin.notifications.recipient-type') }}</th>
            <th>{{ $t('hit-admin.notifications.message-title') }}</th>
            <th>{{ $t('hit-admin.notifications.recipients') }}</th>
            <th>{{ $t('hit-admin.notifications.counts') }}</th>
            <th>{{ $t('hit-admin.notifications.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>{{ formatDate(row.created_at) }}</td>
            <td>
              <span class="status-pill" :class="row.status">{{
                row.status
              }}</span>
            </td>
            <td>{{ recipientLabel(row.recipient_type) }}</td>
            <td class="title-cell">{{ row.title }}</td>
            <td>
              <button
                type="button"
                class="recipient-link"
                :disabled="!(row.recipient_count > 0)"
                @click="openDeliveries(row)"
              >
                {{ row.recipient_count ?? 0 }}
              </button>
            </td>
            <td>
              <div class="counts-cell">
                <div>
                  {{ row.sent_count ?? 0 }} / {{ row.failed_count ?? 0 }}
                </div>
                <div class="counts-channel">
                  {{
                    row.send_email
                      ? $t('hit-admin.notifications.channel-in-app-and-email')
                      : $t('hit-admin.notifications.channel-in-app-only')
                  }}
                </div>
              </div>
            </td>
            <td>
              <hit-button
                :label="$t('hit-admin.notifications.resend')"
                color="button"
                prefix-icon="send"
                :disabled="!canResend(row) || resendingId === row.id"
                :show-spinner="resendingId === row.id"
                @click="resend(row)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="total > pageSize" class="pager">
      <hit-button
        label="Préc."
        color="button"
        :disabled="page <= 1 || loading"
        @click="
          page--;
          reload();
        "
      />
      <span>{{ page }} / {{ totalPages }}</span>
      <hit-button
        label="Suiv."
        color="button"
        :disabled="page >= totalPages || loading"
        @click="
          page++;
          reload();
        "
      />
    </div>

    <notification-broadcast-deliveries-modal
      :open="deliveriesModalOpen"
      :broadcast-id="selectedBroadcastId"
      :broadcast-title="selectedBroadcastTitle"
      @close="deliveriesModalOpen = false"
    />
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';
import NotificationBroadcastDeliveriesModal from './NotificationCampaignDeliveriesModal.vue';

const RECIPIENT_LABELS = {
  all_users: 'Tous les utilisateurs',
  all_admins: 'Tous les admins',
  company_users: 'Utilisateurs société',
  company_admins: 'Admins société',
};

export default defineComponent({
  name: 'NotificationBroadcastList',
  components: {HitButton, NotificationBroadcastDeliveriesModal},
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      resendingId: null,
      deliveriesModalOpen: false,
      selectedBroadcastId: null,
      selectedBroadcastTitle: '',
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    recipientLabel(type) {
      return RECIPIENT_LABELS[type] || type;
    },
    formatDate(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString('fr-BE');
      } catch {
        return iso;
      }
    },
    canResend(row) {
      const s = (row?.status || '').toLowerCase();
      return s === 'completed' || s === 'failed';
    },
    openDeliveries(row) {
      if (!(row?.recipient_count > 0)) return;
      this.selectedBroadcastId = row.id;
      this.selectedBroadcastTitle = row.title || '';
      this.deliveriesModalOpen = true;
    },
    async resend(row) {
      if (!this.canResend(row)) return;
      const count = row.recipient_count ?? 0;
      const msg = this.$t('hit-admin.notifications.resend-confirm', {count});
      if (!window.confirm(msg)) return;

      this.resendingId = row.id;
      try {
        await adminApi.resendNotificationBroadcast(row.id);
        window.alert(this.$t('hit-admin.notifications.resend-success'));
        await this.reload(true);
      } catch (e) {
        window.alert(
          `${this.$t('hit-admin.notifications.resend-failed')}: ${
            e?.message || e
          }`
        );
      } finally {
        this.resendingId = null;
      }
    },
    async reload(silent = false) {
      if (!silent) this.loading = true;
      try {
        const res = await adminApi.listNotificationBroadcasts({
          page: this.page,
          pageSize: this.pageSize,
        });
        this.rows = res?.rows || [];
        this.total = res?.total ?? 0;
      } catch {
        if (!silent) this.rows = [];
      } finally {
        if (!silent) this.loading = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.82);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.panel-title {
  font-weight: 700;
  font-size: 1.05rem;
}

.table-wrap {
  overflow-x: auto;
}

.broadcast-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    padding: 0.5rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  }
  th {
    font-weight: 600;
    color: #475569;
  }
}

.title-cell {
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.counts-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.counts-channel {
  font-size: 0.75rem;
  color: #03695a;
}

.status-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  &.completed {
    background: rgba(0, 153, 124, 0.15);
    color: #03695a;
  }
  &.processing {
    background: rgba(59, 130, 246, 0.15);
    color: #1d4ed8;
  }
  &.failed {
    background: rgba(185, 28, 28, 0.12);
    color: #b91c1c;
  }
}

.pager {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.muted {
  color: #64748b;
}

.recipient-link {
  background: none;
  border: none;
  padding: 0;
  color: #03695a;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  &:disabled {
    color: #94a3b8;
    text-decoration: none;
    cursor: default;
  }
}
</style>
